import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { WebLogger } from "@weps/shared-http";

/**
 * HTTP Logging Interceptor
 *
 * Automatically logs all HTTP requests/responses with:
 * - Backend-aligned event types (api_call_start, api_call_complete, api_call_failed, api_call_exception)
 * - Standard field names (service, api_name, http_method, status_code, duration_ms)
 * - Performance timing
 *
 * All fields are logged in the payload object for Splunk queries.
 */
@Injectable()
export class HttpLoggingInterceptor implements HttpInterceptor {
  private readonly VEX_SERVICE = "vex";

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const startTime = performance.now();

    // Parse service and API name from URL
    const { service, apiName } = this.parseServiceFromUrl(req.url);

    // Log api_call_start (debug level to avoid noise)
    WebLogger.log({
      message: `HTTP ${req.method} started: ${this.sanitizeUrl(req.url)}`,
      level: "debug",
      payload: {
        event_type: "api_call_start",
        service,
        api_name: apiName,
        http_method: req.method,
        http_url: this.sanitizeUrl(req.url),
      },
    });

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            const durationMs = Math.round(performance.now() - startTime);

            // Extract backend trace ID if available (Phase 3 enhancement)
            const backendTraceId = this.extractTraceId(event);
            const backendSpanId = this.extractSpanId(event);

            // Build payload with optional trace context
            const payload: any = {
              event_type: "api_call_complete",
              service,
              api_name: apiName,
              http_method: req.method,
              http_url: this.sanitizeUrl(req.url),
              status_code: event.status,
              duration_ms: durationMs,
            };

            // Add backend trace context if available
            if (backendTraceId) {
              payload.backend_trace_id = backendTraceId;
            }
            if (backendSpanId) {
              payload.backend_span_id = backendSpanId;
            }

            // Log api_call_complete
            WebLogger.log({
              message: `HTTP ${req.method} ${event.status}: ${this.sanitizeUrl(req.url)}`,
              level: "info",
              payload,
            });
          }
        },
        // TODO: Refactor to reduce cyclomatic complexity (currently 12, max 10)
        // eslint-disable-next-line complexity
        error: (error: HttpErrorResponse) => {
          const durationMs = Math.round(performance.now() - startTime);

          // Determine if this is a failed HTTP call (4xx/5xx) or exception (network error)
          const eventType = error.status > 0 ? "api_call_failed" : "api_call_exception";

          // Log api_call_failed or api_call_exception
          WebLogger.log({
            message: `HTTP ${req.method} ${error.status || "ERROR"}: ${this.sanitizeUrl(req.url)}`,
            level: "error",
            payload: {
              event_type: eventType,
              service,
              api_name: apiName,
              http_method: req.method,
              http_url: this.sanitizeUrl(req.url),
              status_code: error.status || 0,
              duration_ms: durationMs,
              error_message: error.message,
              error_type: error.name,
            },
          });

          // WET-specific 403 detection: log a distinct event when VEX rejects a sync
          // because the client does not have active engagement consent.
          // VEX returns messages starting with "Sync not allowed" for consent denials;
          // this distinguishes them from other 403s (auth failures, gatekeeper, etc.)
          // so the team can monitor WET denials separately in Splunk.
          if (error.status === 403 && service === this.VEX_SERVICE) {
            const body = error.error;
            const bodyMessage: string =
              typeof body === "object" && body !== null
                ? (body?.message ?? body?.error?.message ?? "")
                : typeof body === "string"
                  ? body
                  : "";
            if (bodyMessage.includes("Sync not allowed")) {
              WebLogger.log({
                message: "WET consent denied — Vanilla sync blocked by engagement consent check",
                level: "warn",
                payload: {
                  event_type: "wet_consent_denied",
                  service: this.VEX_SERVICE,
                  api_name: apiName,
                  http_method: req.method,
                  http_url: this.sanitizeUrl(req.url),
                  status_code: 403,
                  vex_message: bodyMessage,
                },
              });
            }
          }
        },
      }),
    );
  }

  /**
   * Parse service and API name from URL
   * Matches backend service registry (rob, sgq, wah, ago, aan, etc.)
   */
  // TODO: Refactor to reduce cyclomatic complexity (currently 18, max 10)
  // eslint-disable-next-line complexity
  private parseServiceFromUrl(url: string): { service: string; apiName: string } {
    // ROB GraphQL endpoints
    if (url.includes("/api/weps-profile/get-profile/graphql")) {
      return { service: "rob", apiName: "estate_profile" };
    }
    if (url.includes("/api/weps-profile/subgraph/aan")) {
      return { service: "aan", apiName: "account_aggregation" };
    }
    if (url.includes("/api/weps-profile/subgraph/ago")) {
      return { service: "ago", apiName: "aggregated_outside_accounts" };
    }

    // VEX endpoints
    if (url.includes("/api/vex/status")) {
      return { service: "vex", apiName: "status" };
    }
    if (url.includes("/api/vex/estate-profile")) {
      return { service: "vex", apiName: "estate_profile" };
    }

    // Estate plan tracker
    if (url.includes("/api/estate-plan/tracker")) {
      return { service: "estate_plan", apiName: "tracker" };
    }

    // ZOK endpoints (Zone Of Kinship)
    if (
      url.includes("/api/households/people/sync") ||
      url.includes("/household-members/people/sync")
    ) {
      return { service: "zok", apiName: "sync_people" };
    }
    if (url.includes("/api/households/people") || url.includes("/household-members/people")) {
      return { service: "zok", apiName: "people" };
    }
    if (url.includes("/api/households/members/") || url.includes("/household-members/members/")) {
      return { service: "zok", apiName: "member" };
    }
    if (url.includes("/api/households") || url.includes("/household-members")) {
      return { service: "zok", apiName: "household" };
    }

    // Proxy calls (WEPS proxy)
    if (url.includes("/api/weps-proxy") || url.includes("/api/proxy")) {
      return { service: "proxy", apiName: "weps_proxy" };
    }

    // Gatekeeper
    if (url.includes("/gatekeeper")) {
      return { service: "gatekeeper", apiName: "session_validation" };
    }

    // Default for unknown services - UI-initiated calls
    return { service: "pwi", apiName: "unknown" };
  }

  /**
   * Remove query params and PII from URL
   * Matches backend sanitization pattern
   */
  private sanitizeUrl(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      // Keep only origin + pathname, remove query params and hash
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
      // If URL parsing fails, just remove query string manually
      return url.split("?")[0].split("#")[0];
    }
  }

  /**
   * Extract backend trace ID from response headers (Phase 3 enhancement)
   *
   * Common header names used by backends:
   * - X-Trace-Id
   * - Traceparent (W3C standard)
   * - X-B3-TraceId (Zipkin)
   *
   * Returns null if no trace ID found
   */
  private extractTraceId(response: HttpResponse<any>): string | null {
    // Try common trace ID headers
    const traceIdHeaders = [
      "x-trace-id",
      "traceparent", // W3C Trace Context format: 00-<trace-id>-<span-id>-<flags>
      "x-b3-traceid",
    ];

    for (const header of traceIdHeaders) {
      const value = response.headers.get(header);
      if (value) {
        // Handle W3C Traceparent format
        if (header === "traceparent") {
          const parts = value.split("-");
          if (parts.length >= 2) {
            return parts[1]; // Extract trace-id from traceparent
          }
        }
        return value;
      }
    }

    // Try to extract from GraphQL response extensions
    if (response.body && typeof response.body === "object") {
      const extensions = (response.body as any).extensions;
      if (extensions?.trace_id) {
        return extensions.trace_id;
      }
    }

    return null;
  }

  /**
   * Extract backend span ID from response headers (Phase 3 enhancement)
   *
   * Returns null if no span ID found
   */
  private extractSpanId(response: HttpResponse<any>): string | null {
    // Try common span ID headers
    const spanIdHeaders = [
      "x-span-id",
      "traceparent", // W3C Trace Context
      "x-b3-spanid",
    ];

    for (const header of spanIdHeaders) {
      const value = response.headers.get(header);
      if (value) {
        // Handle W3C Traceparent format
        if (header === "traceparent") {
          const parts = value.split("-");
          if (parts.length >= 3) {
            return parts[2]; // Extract span-id from traceparent
          }
        }
        return value;
      }
    }

    // Try to extract from GraphQL response extensions
    if (response.body && typeof response.body === "object") {
      const extensions = (response.body as any).extensions;
      if (extensions?.span_id) {
        return extensions.span_id;
      }
    }

    return null;
  }
}
